import { useMemo, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search,
  Filter,
  SortAsc,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  MoreVertical,
  FileEdit,
  AlertTriangle,
  Phone,
  MapPin,
  Calendar,
} from 'lucide-react'
import { usePatients } from '@hooks/usePatients'
import { appointmentApi } from '@services/appointment.service'
import { useActivePatient } from '@context/ActivePatientContext'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { calculateAge, getInitials, cn } from '@lib/utils'
import { Patient } from '@/types'
import { CreatePatientDialog } from '@/components/patients/CreatePatientDialog'

// Status badges use design-system semantic colors from DESIGN.md
const statusConfig: Record<string, { labelKey: string; bg: string; text: string; dot: string }> = {
  active: {
    labelKey: 'status.active',
    bg: 'bg-[#2d7a5e]/10',
    text: 'text-[#2d7a5e]',
    dot: 'bg-[#2d7a5e]',
  },
  inactive: {
    labelKey: 'status.inactive',
    bg: 'bg-ml-surface-container',
    text: 'text-ml-on-surface-variant',
    dot: 'bg-ml-outline',
  },
  discharged: {
    labelKey: 'status.discharged',
    bg: 'bg-ml-surface-container',
    text: 'text-ml-on-surface-variant',
    dot: 'bg-ml-outline',
  },
  critical: {
    labelKey: 'status.critical',
    bg: 'bg-ml-error-container',
    text: 'text-ml-error',
    dot: 'bg-ml-error',
  },
  transferred: {
    labelKey: 'status.transferred',
    bg: 'bg-[#0369a1]/10',
    text: 'text-[#0369a1]',
    dot: 'bg-[#0369a1]',
  },
  deceased: {
    labelKey: 'status.inactive',
    bg: 'bg-ml-surface-container',
    text: 'text-ml-on-surface-variant/60',
    dot: 'bg-ml-outline-variant',
  },
}

// Avatar palette uses colors adjacent to the MediLux palette family
const AVATAR_PALETTES = [
  'bg-[#ddf0e8] text-[#1f5c46] ring-[#b8dece]',
  'bg-[#f5e5e2] text-[#8c3020] ring-[#e8c7c0]',
  'bg-[#e3ebea] text-[#2e3f3a] ring-[#c0d3cf]',
  'bg-[#fef0e4] text-[#9a5012] ring-[#f5d8b8]',
  'bg-[#ddeaf5] text-[#1a4f80] ring-[#b8d4eb]',
  'bg-[#ece5f2] text-[#5a2d80] ring-[#d4bfe8]',
]

function avatarPalette(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length]
}

const filterTabs: { key: string; labelKey: string; status?: string }[] = [
  { key: 'all', labelKey: 'common.all' },
  { key: 'active', labelKey: 'status.active', status: 'active' },
  { key: 'inactive', labelKey: 'status.inactive', status: 'inactive' },
  { key: 'transferred', labelKey: 'status.transferred', status: 'transferred' },
]

const NEXT_ACTIVE_STATUSES = new Set([
  'pending_approval',
  'scheduled',
  'confirmed',
  'checked_in',
  'in_progress',
  'rescheduled',
])

// Produces a windowed page range with ellipsis sentinels (-1)
function getPageRange(current: number, total: number): (number | -1)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i)
  const range: (number | -1)[] = [0]
  const left = Math.max(1, current - 1)
  const right = Math.min(total - 2, current + 1)
  if (left > 1) range.push(-1)
  for (let i = left; i <= right; i++) range.push(i)
  if (right < total - 2) range.push(-1)
  range.push(total - 1)
  return range
}

function NextAppointmentCell({ patientId }: { patientId: string }) {
  const { formatDate, formatTime, timezone } = useClinicFormat()
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date())

  const { data, isLoading } = useQuery({
    queryKey: ['patient-next-appointment', patientId, today],
    queryFn: () =>
      appointmentApi.getAppointments({ patient_id: patientId, date_from: today, limit: 50 }),
    staleTime: 60_000,
  })

  const next = useMemo(() => {
    const items = data?.items ?? []
    const cutoff = Date.now() - 60 * 60 * 1000
    return items
      .filter(
        (a) =>
          NEXT_ACTIVE_STATUSES.has(a.status) &&
          new Date(a.appointment_date).getTime() >= cutoff,
      )
      .sort(
        (a, b) =>
          new Date(a.appointment_date).getTime() - new Date(b.appointment_date).getTime(),
      )[0]
  }, [data])

  const { t } = useTranslation()

  if (isLoading) {
    return <div className="h-4 w-28 rounded-md bg-ml-surface-container-high animate-pulse" />
  }
  if (!next) {
    return <span className="text-sm text-ml-on-surface-variant/50">{t('patients.no_next_appointment')}</span>
  }
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium text-ml-on-surface truncate">
        {formatDate(next.appointment_date)}
      </p>
      <p className="text-xs text-ml-on-surface-variant">{formatTime(next.appointment_date)}</p>
    </div>
  )
}

function PatientRowSkeleton({ index }: { index: number }) {
  return (
    <div>
      {/* Mobile skeleton */}
      <div
        className={cn(
          'md:hidden flex items-start gap-3 px-4 py-4',
          index !== 0 && 'border-t border-ml-outline-variant/10',
        )}
      >
        <div className="w-10 h-10 rounded-full bg-ml-surface-container-high animate-pulse flex-shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2 pt-0.5">
          <div className="h-4 w-3/5 rounded-md bg-ml-surface-container-high animate-pulse" />
          <div className="h-3 w-2/5 rounded-md bg-ml-surface-container animate-pulse" />
          <div className="mt-3 pt-2 border-t border-ml-outline-variant/10 h-4 w-4/5 rounded-md bg-ml-surface-container animate-pulse" />
        </div>
      </div>
      {/* Desktop skeleton */}
      <div
        className={cn(
          'hidden md:grid md:grid-cols-[2.5fr_1.5fr_1.1fr_auto] lg:grid-cols-[2.5fr_1fr_1.5fr_1.1fr_auto] items-center gap-5 pl-4 pr-6 py-4',
          index !== 0 && 'border-t border-ml-outline-variant/10',
        )}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-ml-surface-container-high animate-pulse flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-40 rounded-md bg-ml-surface-container-high animate-pulse" />
            <div className="h-3 w-20 rounded-md bg-ml-surface-container animate-pulse" />
          </div>
        </div>
        <div className="hidden lg:block h-3 w-20 rounded-md bg-ml-surface-container animate-pulse" />
        <div className="h-4 w-32 rounded-md bg-ml-surface-container animate-pulse" />
        <div className="h-5 w-16 rounded bg-ml-surface-container animate-pulse" />
        <div className="w-20" />
      </div>
    </div>
  )
}

export function PatientsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { setActivePatient } = useActivePatient()
  const [inputValue, setInputValue] = useState(() => searchParams.get('search') ?? '')
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [page, setPage] = useState(0)
  const [activeFilter, setActiveFilter] = useState('all')

  useEffect(() => {
    const param = searchParams.get('search') ?? ''
    setInputValue(param)
    setSearchQuery(param)
    setPage(0)
  }, [searchParams])

  const handleSearchChange = (value: string) => {
    setInputValue(value)
    setPage(0)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value)
      setSearchParams(value ? { search: value } : {}, { replace: true })
    }, 400)
  }
  const pageSize = 10

  const activeStatus = filterTabs.find((t) => t.key === activeFilter)?.status

  const { data: patientsData, isLoading } = usePatients({
    query: searchQuery || undefined,
    status: activeStatus,
    skip: page * pageSize,
    limit: pageSize,
  })

  const patients = patientsData?.items || []
  const total = patientsData?.total || 0
  const totalPages = Math.ceil(total / pageSize)

  const handleOpenChart = (patient: Patient, e: React.MouseEvent) => {
    e.stopPropagation()
    setActivePatient(patient)
    navigate(`/workspace/patient/${patient.id}/datos-paciente`)
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-6 sm:mb-8">
        <div>
          <h2 className="text-3xl sm:text-4xl font-headline italic text-ml-primary leading-tight">
            {t('patients.title')}
          </h2>
          <p className="text-ml-on-surface-variant text-sm mt-2 max-w-lg">
            {t('patients.subtitle')}
          </p>
        </div>
        <div className="sm:shrink-0">
          <CreatePatientDialog />
        </div>
      </div>

      {/* Main panel */}
      <div className="bg-ml-surface-container-lowest rounded-xl sm:rounded-2xl border border-ml-outline-variant/10 shadow-sm overflow-hidden">

        {/* Top bar: search + filter pills + icon buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 sm:px-6 pt-5 pb-4">
          {/* Search */}
          <div className="relative w-full sm:flex-1 sm:min-w-[220px] sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ml-on-surface-variant/50 pointer-events-none" />
            <input
              placeholder={t('patients.search_placeholder')}
              className="w-full bg-ml-surface-container border border-ml-outline-variant/20 rounded py-2 pl-9 pr-4 text-sm font-medium text-ml-on-surface placeholder:text-ml-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-ml-primary/20 focus:border-ml-primary/30 transition-all"
              value={inputValue}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          {/* Filter pills + icon buttons */}
          <div className="flex items-center gap-2 sm:flex-1 overflow-hidden">
            {/* Filter pills — horizontally scrollable on mobile */}
            <div className="flex items-center gap-1.5 overflow-x-auto flex-nowrap scrollbar-hide flex-1 pb-0.5 -mb-0.5">
              {filterTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => { setActiveFilter(tab.key); setPage(0) }}
                  className={cn(
                    'px-3.5 py-1.5 rounded text-xs font-bold tracking-wide transition-all duration-200 min-h-0 flex-shrink-0',
                    activeFilter === tab.key
                      ? 'bg-ml-primary text-white shadow-sm'
                      : 'bg-ml-surface-container text-ml-on-surface-variant hover:bg-ml-surface-container-high hover:text-ml-on-surface'
                  )}
                >
                  {t(tab.labelKey)}
                </button>
              ))}
            </div>

            {/* Icon buttons */}
            <div className="flex items-center gap-2 shrink-0">
              <button className="p-2 rounded-lg bg-ml-surface-container hover:bg-ml-surface-container-high transition-colors min-h-0 text-ml-on-surface-variant hover:text-ml-on-surface">
                <Filter className="h-4 w-4" />
              </button>
              <button className="p-2 rounded-lg bg-ml-surface-container hover:bg-ml-surface-container-high transition-colors min-h-0 text-ml-on-surface-variant hover:text-ml-on-surface">
                <SortAsc className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Patient list */}
        {isLoading ? (
          <div className="border-t border-ml-outline-variant/10">
            {Array.from({ length: 5 }, (_, i) => (
              <PatientRowSkeleton key={i} index={i} />
            ))}
          </div>
        ) : patients.length === 0 ? (
          <div className="text-center py-20 border-t border-ml-outline-variant/10">
            <div className="h-14 w-14 rounded-2xl bg-ml-surface-container flex items-center justify-center mx-auto mb-5">
              <UserPlus className="h-7 w-7 text-ml-on-surface-variant/40" />
            </div>
            <h3 className="text-base font-semibold text-ml-on-surface mb-1.5">
              {t('patients.no_results')}
            </h3>
            <p className="text-sm text-ml-on-surface-variant max-w-xs mx-auto leading-relaxed">
              {searchQuery ? t('patients.adjust_search') : t('patients.start_adding')}
            </p>
          </div>
        ) : (
          <>
            {/* Column headers — desktop/tablet only */}
            <div className="hidden md:grid md:grid-cols-[2.5fr_1.5fr_1.1fr_auto] lg:grid-cols-[2.5fr_1fr_1.5fr_1.1fr_auto] items-center gap-5 pl-8 pr-6 py-3 border-y border-ml-outline-variant/10 bg-ml-surface-container-low/50">
              <span className="ml-label">{t('patients.col_name')}</span>
              <span className="ml-label hidden lg:block">{t('patients.col_id')}</span>
              <span className="ml-label">{t('patients.col_next_appointment')}</span>
              <span className="ml-label">{t('common.status')}</span>
              <span className="w-20" />
            </div>

            <div>
              {patients.map((patient: Patient, idx: number) => {
                const status = statusConfig[patient.status as keyof typeof statusConfig] || statusConfig.inactive
                const palette = avatarPalette(patient.full_name)
                const allergyCount = (patient.allergies ?? []).filter(a => a?.name).length
                const conditions = patient.chronic_conditions ?? []

                const handleNavigate = () => {
                  setActivePatient(patient)
                  navigate(`/workspace/patient/${patient.id}/datos-paciente`)
                }

                const avatarBlock = (
                  <div className="relative flex-shrink-0">
                    {patient.photo_url ? (
                      <img
                        src={patient.photo_url}
                        alt={patient.full_name}
                        className="w-10 h-10 rounded-full object-cover ring-1 ring-ml-outline-variant/20"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                          e.currentTarget.nextElementSibling?.removeAttribute('hidden')
                        }}
                      />
                    ) : null}
                    <div
                      className={cn('w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ring-1', palette)}
                      hidden={!!patient.photo_url}
                    >
                      {getInitials(patient.first_name, patient.last_name)}
                    </div>
                    {patient.blood_type && (
                      <span className="absolute -bottom-1 -right-1.5 px-1 py-px rounded text-[10px] font-black bg-white border border-ml-outline-variant/30 text-ml-on-surface shadow-sm leading-none">
                        {patient.blood_type}
                      </span>
                    )}
                  </div>
                )

                return (
                  <div key={patient.id}>

                    {/* ── Mobile card (< md) ── */}
                    <div
                      className={cn(
                        'md:hidden flex items-start gap-3 px-4 py-4 cursor-pointer',
                        'active:bg-ml-surface-container-low/60 transition-colors',
                        idx !== 0 && 'border-t border-ml-outline-variant/10'
                      )}
                      onClick={handleNavigate}
                    >
                      <div className="mt-0.5">{avatarBlock}</div>
                      <div className="flex-1 min-w-0">
                        {/* Name row + status + more */}
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h3 className="text-sm font-semibold text-ml-on-surface truncate leading-tight">
                                {patient.full_name}
                              </h3>
                              {allergyCount > 0 && (
                                <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-px rounded text-[10px] font-bold bg-ml-error-container text-ml-error border border-ml-error/20">
                                  <AlertTriangle className="h-2.5 w-2.5" />
                                  {allergyCount}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <span className="text-xs text-ml-on-surface-variant/70">
                                {t('patients.years_old', { age: calculateAge(patient.date_of_birth) })}
                              </span>
                              {patient.city && (
                                <>
                                  <span className="text-ml-on-surface-variant/30 text-xs">·</span>
                                  <span className="flex items-center gap-0.5 text-[11px] text-ml-on-surface-variant/55">
                                    <MapPin className="h-2.5 w-2.5" />
                                    {patient.city}
                                  </span>
                                </>
                              )}
                              {patient.phone_primary && !patient.city && (
                                <>
                                  <span className="text-ml-on-surface-variant/30 text-xs">·</span>
                                  <span className="flex items-center gap-0.5 text-[11px] text-ml-on-surface-variant/55">
                                    <Phone className="h-2.5 w-2.5" />
                                    {patient.phone_primary}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold rounded', status.bg, status.text)}>
                              <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', status.dot)} />
                              {t(status.labelKey)}
                            </span>
                            <button
                              className="p-1.5 rounded-lg hover:bg-ml-surface-container-high text-ml-on-surface-variant transition-colors min-h-0"
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Más opciones"
                            >
                              <MoreVertical className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Conditions */}
                        {conditions.length > 0 && (
                          <div className="flex gap-1 mt-1.5 flex-wrap">
                            {conditions.slice(0, 2).map((cond, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-px rounded bg-ml-surface-container border border-ml-outline-variant/20 text-ml-on-surface-variant/65 font-medium truncate max-w-[90px]">
                                {cond}
                              </span>
                            ))}
                            {conditions.length > 2 && (
                              <span className="text-[10px] px-1.5 py-px rounded bg-ml-surface-container border border-ml-outline-variant/20 text-ml-on-surface-variant/50 font-medium">
                                +{conditions.length - 2}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Next appointment footer */}
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-ml-outline-variant/10">
                          <Calendar className="h-3 w-3 text-ml-on-surface-variant/40 flex-shrink-0" />
                          <NextAppointmentCell patientId={patient.id} />
                        </div>
                      </div>
                    </div>

                    {/* ── Desktop/tablet row (md+) ── */}
                    <div
                      className={cn(
                        'group hidden md:grid md:grid-cols-[2.5fr_1.5fr_1.1fr_auto] lg:grid-cols-[2.5fr_1fr_1.5fr_1.1fr_auto] items-center gap-5 pl-4 pr-6 py-4',
                        'hover:bg-ml-surface-container-low/60 transition-all duration-150 cursor-pointer',
                        idx !== 0 && 'border-t border-ml-outline-variant/10'
                      )}
                      onClick={handleNavigate}
                    >
                      {/* Name + avatar + badges */}
                      <div className="flex items-center gap-3 min-w-0">
                        {avatarBlock}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <h3 className="text-sm font-semibold text-ml-on-surface truncate leading-tight">
                              {patient.full_name}
                            </h3>
                            {allergyCount > 0 && (
                              <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-px rounded text-[10px] font-bold bg-ml-error-container text-ml-error border border-ml-error/20">
                                <AlertTriangle className="h-2.5 w-2.5" />
                                {allergyCount}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className="text-xs text-ml-on-surface-variant/70">
                              {t('patients.years_old', { age: calculateAge(patient.date_of_birth) })}
                            </span>
                            {patient.city && (
                              <>
                                <span className="text-ml-on-surface-variant/30 text-xs">·</span>
                                <span className="flex items-center gap-0.5 text-[11px] text-ml-on-surface-variant/55">
                                  <MapPin className="h-2.5 w-2.5" />
                                  {patient.city}
                                </span>
                              </>
                            )}
                            {patient.phone_primary && !patient.city && (
                              <>
                                <span className="text-ml-on-surface-variant/30 text-xs">·</span>
                                <span className="flex items-center gap-0.5 text-[11px] text-ml-on-surface-variant/55">
                                  <Phone className="h-2.5 w-2.5" />
                                  {patient.phone_primary}
                                </span>
                              </>
                            )}
                          </div>
                          {conditions.length > 0 && (
                            <div className="flex gap-1 mt-1.5 flex-wrap">
                              {conditions.slice(0, 2).map((cond, i) => (
                                <span key={i} className="text-[10px] px-1.5 py-px rounded bg-ml-surface-container border border-ml-outline-variant/20 text-ml-on-surface-variant/65 font-medium truncate max-w-[90px]">
                                  {cond}
                                </span>
                              ))}
                              {conditions.length > 2 && (
                                <span className="text-[10px] px-1.5 py-px rounded bg-ml-surface-container border border-ml-outline-variant/20 text-ml-on-surface-variant/50 font-medium">
                                  +{conditions.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* MRN — hidden at md, shown at lg */}
                      <div className="hidden lg:block min-w-0">
                        <span className="text-xs font-mono text-ml-on-surface-variant">
                          {patient.mrn || patient.id.slice(0, 8)}
                        </span>
                        {patient.phone_primary && patient.city && (
                          <p className="flex items-center gap-0.5 text-[11px] text-ml-on-surface-variant/50 mt-0.5">
                            <Phone className="h-2.5 w-2.5 flex-shrink-0" />
                            <span className="truncate">{patient.phone_primary}</span>
                          </p>
                        )}
                      </div>

                      {/* Próxima cita */}
                      <NextAppointmentCell patientId={patient.id} />

                      {/* Estado */}
                      <div>
                        <span className={cn(
                          'inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold rounded tracking-wide',
                          status.bg, status.text
                        )}>
                          <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', status.dot)} />
                          {t(status.labelKey)}
                        </span>
                      </div>

                      {/* Acciones */}
                      <div className="flex items-center gap-1 patient-row-actions opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-ml-primary/8 text-ml-on-surface-variant hover:text-ml-primary transition-colors min-h-0"
                          onClick={(e) => handleOpenChart(patient, e)}
                          title={t('patients.open_chart')}
                          aria-label={t('patients.open_chart')}
                        >
                          <FileEdit className="h-3.5 w-3.5" />
                        </button>
                        <button
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-ml-surface-container-high text-ml-on-surface-variant transition-colors min-h-0"
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Más opciones"
                        >
                          <MoreVertical className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-ml-outline-variant/10">
              <span className="text-xs text-ml-on-surface-variant font-medium">
                <span className="sm:hidden">
                  {page + 1} / {totalPages || 1}
                </span>
                <span className="hidden sm:inline">
                  {t('patients.showing', {
                    from: page * pageSize + 1,
                    to: Math.min((page + 1) * pageSize, total),
                    total: total.toLocaleString('es-MX'),
                  })}
                </span>
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-ml-surface-container-low text-ml-on-surface hover:bg-ml-surface-container-high transition-colors min-h-0 disabled:opacity-30"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  aria-label="Página anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {/* Page numbers — hidden on mobile */}
                <div className="hidden sm:flex items-center gap-1">
                  {getPageRange(page, totalPages).map((item, i) =>
                    item === -1 ? (
                      <span key={`ellipsis-${i}`} className="text-ml-on-surface-variant/50 px-1 text-xs">…</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setPage(item)}
                        className={cn(
                          'w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold min-h-0 transition-colors',
                          page === item
                            ? 'bg-ml-primary text-white'
                            : 'hover:bg-ml-surface-container-high text-ml-on-surface-variant font-medium'
                        )}
                      >
                        {item + 1}
                      </button>
                    )
                  )}
                </div>

                <button
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-ml-surface-container-low text-ml-on-surface hover:bg-ml-surface-container-high transition-colors min-h-0 disabled:opacity-30"
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * pageSize >= total}
                  aria-label="Página siguiente"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
